// slice 02.1 · paso b.6a — funciones puras del canvas de roadmap (`ComponentTaskFlow.jsx`):
// matemática de escala/zoom, trazo de líneas dibujadas a mano, color por etapa, número de
// semana, parseo de la forma de nodo que llega del servidor, y la resolución de posición
// durante un arrastre en grupo. Extraídas verbatim — sin dependencias del componente, mismo
// criterio que `taskGrid.js`.

export const DAY_WIDTH = 200;
export const CANVAS_HEIGHT = 5600;

export const clampScale = (s) => Math.max(0.1, Math.min(s, 5));

export const getSmoothPath = (pts) => {
    if (!pts || pts.length === 0) return '';
    if (pts.length === 1) return `M ${pts[0].x},${pts[0].y}`;
    let d = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length - 1; i++) {
        const xc = (pts[i].x + pts[i + 1].x) / 2;
        const yc = (pts[i].y + pts[i + 1].y) / 2;
        d += ` Q ${pts[i].x},${pts[i].y} ${xc},${yc}`;
    }
    d += ` L ${pts[pts.length - 1].x},${pts[pts.length - 1].y}`;
    return d;
};

export const getStageColor = (stage) => {
    const s = stage ? String(stage).toLowerCase() : '';
    if (s === 'planeada' || s === 'planeado' || s === 'planeacion') return '#9ca3af'; // gris
    if (s === 'en proceso') return '#60a5fa'; // azul
    if (s === 'check') return '#eab308'; // amarillo
    if (s === 'synk') return '#a855f7'; // morado
    if (s === 'done') return '#10b981'; // verde
    if (s === 'bloqueo' || s === 'block') return '#ef4444'; // rojo
    if (s === 'retraso') return '#f97316'; // naranja
    return '#ccc';
};

export const getWeekNumber = (d) => {
    const date = new Date(d.getTime());
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

// Parsea la forma que trae `GET /api/spaces/:id/roadmap_nodes` (contenido serializado como
// string con un prefijo `T:`/`L:`/`C:`) a la forma que usa el estado del canvas (`x`/`y`/`w`/`h`
// + campos propios de cada categoría). Verbatim del bloque que vivía inline en el efecto de
// fetch de `ComponentTaskFlow.jsx`.
export const parseRoadmapNode = (n) => {
    if (n.type === 'texto' || n.category === 'texto') {
        let parsed = { t: n.content, c: '#ffffff', s: 16, sub: 'texto-simple', url: '' };
        if (n.content && n.content.startsWith('T:')) {
            try { parsed = JSON.parse(n.content.substring(2)); } catch (e) { }
        }
        return { ...n, x: n.pos_x, y: n.pos_y, w: n.width, h: n.height, label: parsed.t, colorToken: parsed.c, fontSize: parsed.s, sub: parsed.sub, url: parsed.url || '' };
    }
    if (n.type === 'formas' || n.category === 'formas') {
        let parsed = { pts: [], c: '#ffffff' };
        if (n.content && n.content.startsWith('L:')) {
            try { parsed = JSON.parse(n.content.substring(2)); } catch (e) { }
        }
        return { ...n, category: 'formas', type: 'formas', x: n.pos_x, y: n.pos_y, w: n.width, h: n.height, sub: 'linea', points: parsed.pts, colorToken: parsed.c };
    }
    let label = n.type !== 'image' ? n.content : undefined;
    let colorToken = 'pink';
    if (label && label.startsWith('C:')) {
        const pipeIdx = label.indexOf('|');
        if (pipeIdx > -1) {
            colorToken = label.substring(2, pipeIdx);
            label = label.substring(pipeIdx + 1);
        }
    }
    return {
        ...n,
        x: n.pos_x,
        y: n.pos_y,
        w: n.width,
        h: n.height,
        src: n.type === 'image' ? n.content : undefined,
        label,
        colorToken
    };
};

// De-duplica el snippet `isNodeMoving`/`renderX`/`renderY` que se repetía verbatim en cada una
// de las 5 ramas de renderizado de nodo + una 6ª variante para barras de tarea. Los componentes
// hoja reciben `x`/`y`/`isMoving` ya resueltos -- nunca el `groupMove` crudo (pasar el objeto
// crudo rompería `memo` en cada nodo en cada frame de arrastre, porque `groupMove` es una
// referencia nueva en cada mousemove).
export const resolveNodeRenderPosition = (node, groupMove) => {
    const moving = groupMove?.initialPositions?.[node.id];
    return moving
        ? { x: moving.x + groupMove.dx, y: moving.y + groupMove.dy, isMoving: true }
        : { x: node.x, y: node.y, isMoving: false };
};
